<template>
    <v-app>
        <div class="demo-wrapper">
            <backdrop-demo />

            <transition name="fade">
                <div>
                    <div
                        v-if="!submitted && !thank_you_shown"
                        class="form-container text-center"
                    >
                        <v-row>
                            <v-col
                                cols="5"
                                class="side-by-side-image-wrapper"
                                v-if="$vuetify.breakpoint.lgAndUp"
                            >
                                <img
                                    src="/app/guest-checkin-images/wine-glass-pour.jpg"
                                    class="side-by-side-image"
                                    alt=""
                                />
                            </v-col>

                            <v-col
                                cols="12"
                                lg="7"
                            >
                                <branding-header />

                                <h3 class="heading">Welcome to Barrel Creek Vineyards!</h3>
                                <h4 class="subheading">Check in to get your visit started</h4>

                                <form-demo
                                    ref="form"
                                    v-model="form_data"
                                    @submit="submitForm"
                                >
                                    <template #email>
                                        <v-text-field
                                            v-model="form_data.email"
                                            label="Email Address (optional)"
                                            type="email"
                                            autocomplete="off"
                                            outlined
                                            prepend-inner-icon="mdi-email"
                                            validate-on-blur
                                            class="ma-0 mb-3"
                                            hide-details
                                            :rules="[rules.optional_email_verification]"
                                        />

                                        <transition name="fade">
                                            <div class="disclaimer text-left mb-8">
                                                By providing my email address and submitting this form, I give Barrel
                                                Creek
                                                Vineyards permission to email me with info and offers. Use is subject to
                                                <a
                                                    href="#"
                                                    @click.prevent="$refs.placeholderDialog.show('Terms & Conditions')"
                                                >Terms</a>
                                                with an arbitration clause and a <a
                                                    href="#"
                                                    @click.prevent="$refs.placeholderDialog.show('Privacy Policy')"
                                                >Privacy Policy</a>.
                                            </div>
                                        </transition>
                                    </template>

                                    <template #phone>
                                        <v-text-field
                                            v-model="form_data.phone"
                                            label="Mobile Phone Number (optional)"
                                            type="tel"
                                            autocomplete="off"
                                            validate-on-blur
                                            hide-details
                                            outlined
                                            prepend-inner-icon="mdi-cellphone"
                                            class="mt-2 mb-4 pt-0"
                                            @input="onInputPhone"
                                            :rules="[rules.optional_phone_verification]"
                                        />

                                        <transition name="fade">
                                            <div class="disclaimer text-left mb-8">
                                                By providing my phone number and submitting this form, I give Barrel
                                                Creek
                                                Vineyards
                                                permission to send text messages with info, promotions, and offers to
                                                the number
                                                I
                                                have
                                                provided,
                                                including use of an automated system. Consent is not a condition of
                                                purchase.
                                                Texts
                                                are
                                                recorded.
                                                Message and/or data rates may apply. Message frequency varies. Text STOP
                                                to opt
                                                out
                                                or
                                                HELP for
                                                help at any time. Use is subject to <a
                                                    href="#"
                                                    @click.prevent="$refs.placeholderDialog.show('Terms & Conditions')"
                                                >Terms</a>
                                                with an arbitration clause and a <a
                                                    href="#"
                                                    @click.prevent="$refs.placeholderDialog.show('Privacy Policy')"
                                                >Privacy Policy</a>.
                                            </div>
                                        </transition>
                                    </template>
                                </form-demo>
                            </v-col>
                        </v-row>
                    </div>

                    <thank-you-demo
                        v-else-if="thank_you_shown"
                        :form_data="form_data"
                        :has_flow_demo_after="false"
                        @reset-form="backToForm"
                    />
                </div>
            </transition>

            <placeholder-dialog ref="placeholderDialog" />
        </div>
    </v-app>
</template>

<script>
import BackdropDemo from '@/components/demos/shared/backdrop.vue';
import BrandingHeader from '@/components/demos/shared/branding-header.vue';
import FormDemo from '@/components/demos/guest-checkin/form-demo.vue';
import ThankYouDemo from '@/components/demos/guest-checkin/thank-you-demo.vue';
import PlaceholderDialog from '@/components/demos/shared/placeholder-dialog.vue';
import Validate from '@/js/validate.js';

export default {
    components: {
        BackdropDemo,
        BrandingHeader,
        FormDemo,
        ThankYouDemo,
        PlaceholderDialog
    },

    data() {
        return {
            submitted: false,
            thank_you_shown: false,
            form_data: {
                first_name: '',
                last_name: '',
                birth_date: null,
                email: '',
                phone: ''
            },
            rules: {
                required: value => !!value || 'This field is required',
                optional_phone_verification: value => {
                    if (!value || value.trim() === '') {
                        return true;
                    }

                    if (!Validate.isUSPhoneNumber(value?.trim())) {
                        return 'A valid phone number is required';
                    }
                    return true;
                },
                optional_email_verification: value => {
                    if (!value || value.trim() === '') {
                        return true;
                    }

                    if (!Validate.isEmailAddress(value?.trim())) {
                        return 'Please enter a valid email address';
                    }
                    return true;
                }
            }
        }
    },

    methods: {
        onInputPhone(value) {
            const updated = { ...this.form_data, phone: this.$rc.maskPhoneNumber(value) };
            this.form_data = updated;
        },

        submitForm() {
            if (this.$refs.form?.validate()) {
                this.submitted = true;
                this.scrollToTop();
                this.showThankYou();
            }
        },

        showThankYou() {
            this.thank_you_shown = true;
        },

        resetForm() {
            this.form_data = {
                first_name: '',
                last_name: '',
                birth_date: null,
                email: '',
                phone: ''
            };
            if (this.$refs.form) {
                this.$refs.form.resetValidation();
            }
        },

        backToForm() {
            this.resetForm();
            this.submitted = false;
            this.scrollToTop();
            this.thank_you_shown = false;
        },

        scrollToTop() {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    },

    mounted() {
        document.title = "Guest Check-In | Default";
    }
}
</script>

<style lang="scss" scoped>
.demo-wrapper {
    --brand-primary: #40728F;
    --v-primary-base: var(--brand-primary);
    --v-anchor-base: var(--brand-primary);
    --max-width: 1200px;

    position: relative;
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;


    .side-by-side-image-wrapper {
        position: relative;
        overflow: hidden;
    }

    .side-by-side-image {
        object-fit: cover;
        object-position: 70%;
        max-width: 100%;
        width: 100%;
        height: 100%;
        border-radius: 0.5rem;
    }

    .heading {
        font-size: 1.5rem;
        font-weight: 600;
        margin-top: 0.5rem;
        margin-bottom: 0.25rem;
        color: var(--v-primary-base);
    }

    .subheading {
        font-size: 1.2rem;
        font-weight: 500;
        margin-bottom: 1.5rem;
        color: #666666;
    }

    .form-container {
        position: relative;
        max-width: min(var(--max-width), 90%);
        margin: 2rem auto;
        padding: 2rem;
        background: rgba(255, 255, 255, 0.95);
        border-radius: 1rem;
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
        backdrop-filter: blur(10px);
        border: 1px solid rgba(255, 255, 255, 0.2);
        border-top: 4px solid var(--v-primary-base);

        @media screen and (max-width: 600px) {
            margin: 1rem auto;
            padding: 1.5rem;
            max-width: 95%;
        }
    }

    .disclaimer {
        color: rgba(0, 0, 0, 0.6);
        font-style: italic;
        font-size: 1rem; // must be same size as input labels
    }
}
</style>